import { useCallback, useState } from 'react';
import { STEPS } from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/constants';
import {
    DeclareWinnerDialogForm,
    StepId,
} from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/types';
import { shouldEnterWinnerInfo } from 'components/pages/campaign-settings/campaign-auction/item-details/declare-winner/utils';
import { useCurrentCampaignItem } from 'customHooks/useCurrentCampaignItem';

const useItemWinnerStep = () => {
    const { data: item } = useCurrentCampaignItem();
    const startingStep = shouldEnterWinnerInfo(item)
        ? STEPS.ENTER_WINNER_INFO
        : STEPS.DECLARE_WINNER;
    const [currentStep, setCurrentStep] = useState<StepId>(startingStep);

    const canProgress = useCallback(
        (formData: DeclareWinnerDialogForm) => {
            switch (currentStep) {
                case STEPS.DECLARE_WINNER:
                    return true;
                case STEPS.ENTER_WINNER_INFO:
                    return formData.selectedBidder !== null && formData.selectedBidder.length > 0;
                case STEPS.CHOOSE_PAYMENT:
                    return Boolean(formData.paymentOption);
                case STEPS.FINALIZE_WINNER:
                    if (formData.paymentOption === 'charge') {
                        return Boolean(formData.paymentMethodId);
                    }

                    if (formData.paymentOption === 'offline') {
                        return Boolean(formData.offlinePaymentMethod);
                    }

                    return true;
                default:
                    return false;
            }
        },
        [currentStep],
    );

    const nextStep = useCallback(() => {
        switch (currentStep) {
            case STEPS.DECLARE_WINNER:
            case STEPS.ENTER_WINNER_INFO:
                setCurrentStep(STEPS.CHOOSE_PAYMENT);
                break;
            case STEPS.CHOOSE_PAYMENT:
                setCurrentStep(STEPS.FINALIZE_WINNER);
                break;
            case STEPS.FINALIZE_WINNER:
            default:
                return currentStep;
        }
    }, [currentStep]);

    const prevStep = useCallback(() => {
        if (currentStep === STEPS.CHOOSE_PAYMENT) {
            setCurrentStep(startingStep);
        } else if (currentStep === STEPS.FINALIZE_WINNER) {
            setCurrentStep(STEPS.CHOOSE_PAYMENT);
        }
    }, [currentStep, startingStep]);

    const isFirstStep =
        currentStep === STEPS.DECLARE_WINNER || currentStep === STEPS.ENTER_WINNER_INFO;

    const resetStep = useCallback(() => {
        setCurrentStep(startingStep);
    }, [startingStep]);

    return {
        currentStep,
        setCurrentStep,
        canProgress,
        nextStep,
        prevStep,
        isFirstStep,
        resetStep,
        startingStep,
    };
};

export { useItemWinnerStep };
